import { fetchHTML } from '@utils';
import { Loader, pubsub } from '@services';
import './base-swiper.js';
import { d as debounce } from './debounce-Dg7s1ivP.js';

class PredictiveSearchResults extends HTMLElement {
  /** @type {Array<() => void>} Pubsub unsubscribe callbacks registered in connectedCallback. */
  #unsubscribers = [];

  /** @type {string | undefined} ID of the parent drawer-element, if present. */
  #drawerId = this.closest('drawer-element')?.id;

  /** @type {AbortController | undefined} Aborts in-flight search requests when a new search starts. */
  #abortController;

  /** @type {{ minimumSearchTermLength: number, searchId: string | undefined, hideElement: string | undefined, resourcesLimit: number, resourcesLimitScope: string }} Search configuration derived from data attributes. */
  #options = {
    minimumSearchTermLength: 2,
    searchId: this.dataset.searchId,
    hideElement: this.dataset.hideElement,
    resourcesLimit: 10,
    resourcesLimitScope: 'each',
  };

  /** @type {HTMLElement | null} The search input element linked via data-search-id. */
  #search = document.querySelector(`#${this.#options.searchId}`);

  /** @type {HTMLElement | null | undefined} Element to hide while search results are visible. */
  #elementsToHide;

  /** @type {InstanceType<typeof Loader>} Loading indicator shown while fetching results. */
  #loader = new Loader(this);

  /** Subscribes to search input, clear, and drawer events when a linked search input exists. */
  connectedCallback() {
    // Get the search form
    if (!this.#search) return;

    if (this.#options.hideElement) {
      this.#elementsToHide = document.querySelector(`#${this.#options.hideElement}`);
    }

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.#search.clearSearchInput();
      }
    });

    // Handle search input
    this.#unsubscribers.push(
      pubsub.subscribe('search:input', ({ id, data: { value } }) => {
        if (id !== this.dataset.searchId) return;
        this.#updateSearch(value);
      }),
      pubsub.subscribe('search:clear', ({ id }) => {
        id === this.dataset.searchId && this.#onClearSearchInput();
      }),
      this.#drawerId &&
        pubsub.subscribe('drawer:open', ({ id }) => {
          if (id !== this.#drawerId) return;
          this.#search.clearSearchInput();
          this.#clearSearchResults();
        })
    );
  }

  /** Unsubscribes from all pubsub listeners registered in connectedCallback. */
  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.#loader.destroy();
  }

  /** Empties the results container markup. */
  #clearSearchResults() {
    this.innerHTML = '';
  }

  /** Hides the results panel and restores any elements hidden during search. */
  #onClearSearchInput() {
    this.ariaHidden = true;
    this.#hideOnSearchResults(false);
  }

  /** Debounced handler that fetches results once the user pauses typing. */
  #updateSearch = debounce((searchTerm) => this.#getSearchResults(searchTerm), 300);

  /**
   * Validates the search term length and either clears results or triggers a fetch.
   * @param {string} searchTerm
   */
  #getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.#options.minimumSearchTermLength) {
      this.#clearSearchResults();
      this.#hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.#fetchSearchResults(searchTerm);
  }

  /**
   * Toggles aria-hidden on companion elements while results are shown or cleared.
   * @param {boolean} [hide=true] Whether to hide the companion elements.
   */
  #hideOnSearchResults(hide = true) {
    if (this.#elementsToHide) this.#elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetches predictive search HTML from the Shopify section endpoint and injects it.
   * @param {string} searchTerm
   */
  async #fetchSearchResults(searchTerm) {
    this.#loader.show();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const html = await fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search&resources[limit]=${this.#options.resourcesLimit}&resources[limit_scope]=${this.#options.resourcesLimitScope}&resources[options][fields]=title,product_type,body,variants.title`,
        { signal: this.#abortController.signal }
      );

      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = resultsMarkup;
      this.ariaHidden = false;
      this.#hideOnSearchResults(true);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.#loader.hide();
    }
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
